/**
 * News tab component
 */

import { useState, useMemo, memo } from 'react';
import { useCompanyNews } from '../../../hooks/useCompanies';
import { NewsDetailModal } from '../NewsDetailModal';
import { VerificationBadge } from '../../common/VerificationBadge';
import type { EventListItem, Materiality, VerificationStatus } from '../../../types';

interface NewsTabProps {
  companyId: string;
}

const materialityColors: Record<Materiality, { bg: string; text: string; dot: string; selectedBg: string }> = {
  critical: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500', selectedBg: 'bg-red-100' },
  high: { bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-500', selectedBg: 'bg-orange-100' },
  medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500', selectedBg: 'bg-yellow-100' },
  low: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-400', selectedBg: 'bg-blue-100' },
};

const eventTypeLabels: Record<string, string> = {
  m_and_a_announced: 'M&A Announced',
  m_and_a_completed: 'M&A Completed',
  leadership_change: 'Leadership Change',
  regulatory_action: 'Regulatory',
  earnings_release: 'Earnings',
  funding_round: 'Funding',
  major_partnership: 'Partnership',
  product_launch: 'Product Launch',
  product_update: 'Product Update',
  stock_movement: 'Stock Movement',
  workforce_change: 'Workforce',
  litigation: 'Litigation',
  press_mention: 'Press',
  job_posting: 'Job Posting',
};

function MaterialityBadge({ materiality }: { materiality: Materiality }) {
  const colors = materialityColors[materiality];
  return (
    <span
      className={`inline-flex items-center rounded-full ${colors.bg} px-2.5 py-0.5 text-xs font-medium ${colors.text}`}
    >
      <span className={`mr-1.5 h-1.5 w-1.5 rounded-full ${colors.dot}`} />
      {materiality.charAt(0).toUpperCase() + materiality.slice(1)}
    </span>
  );
}

function SourceTierBadge({ tier }: { tier: number }) {
  const tierLabels: Record<number, string> = {
    1: 'Tier 1',
    2: 'Tier 2',
    3: 'Tier 3',
    4: 'Tier 4',
  };

  const tierColors: Record<number, string> = {
    1: 'text-green-600',
    2: 'text-blue-600',
    3: 'text-yellow-600',
    4: 'text-gray-600',
  };

  return (
    <span className={`text-xs font-medium ${tierColors[tier] || tierColors[4]}`}>
      {tierLabels[tier] || `Tier ${tier}`}
    </span>
  );
}

/**
 * PERF: Memoized news item to prevent re-renders when filters change.
 * Event data is immutable from API, so shallow comparison is sufficient.
 */
const NewsItem = memo(function NewsItem({
  event,
  onClick,
}: {
  event: EventListItem;
  onClick: () => void;
}) {
  const publishedDate = new Date(event.published_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div
      className="rounded-lg border border-gray-200 bg-white p-4 hover:shadow-md hover:border-primary-200 transition-all cursor-pointer group"
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <MaterialityBadge materiality={event.materiality} />
            <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              {eventTypeLabels[event.event_type] || event.event_type}
            </span>
            {event.is_official && (
              <span className="inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                Official
              </span>
            )}
            {event.verification_status && event.verification_status !== 'unverified' && (
              <VerificationBadge
                status={event.verification_status as VerificationStatus}
                showLabel={false}
                size="sm"
              />
            )}
          </div>
          <h3 className="text-base font-medium text-gray-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {event.title}
          </h3>
          {event.summary && (
            <p className="mt-2 text-sm text-gray-600 line-clamp-2">{event.summary}</p>
          )}
          <div className="mt-3 flex items-center gap-3 text-xs text-gray-500">
            <span>{event.source_name}</span>
            <span>•</span>
            <SourceTierBadge tier={event.source_tier} />
            <span>•</span>
            <span>{publishedDate}</span>
            <span className="ml-auto text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity">
              Click for analysis →
            </span>
          </div>
        </div>
      </div>
    </div>
  );
});

function NewsFilter({
  selectedMateriality,
  onMaterialityChange,
  selectedEventTypes,
  onEventTypeChange,
  availableEventTypes,
}: {
  selectedMateriality: Materiality[];
  onMaterialityChange: (levels: Materiality[]) => void;
  selectedEventTypes: string[];
  onEventTypeChange: (types: string[]) => void;
  availableEventTypes: string[];
}) {
  const levels: Materiality[] = ['critical', 'high', 'medium', 'low'];

  const toggleMateriality = (level: Materiality) => {
    if (selectedMateriality.includes(level)) {
      onMaterialityChange(selectedMateriality.filter((m) => m !== level));
    } else {
      onMaterialityChange([...selectedMateriality, level]);
    }
  };

  const toggleEventType = (type: string) => {
    if (selectedEventTypes.includes(type)) {
      onEventTypeChange(selectedEventTypes.filter((t) => t !== type));
    } else {
      onEventTypeChange([...selectedEventTypes, type]);
    }
  };

  return (
    <div className="space-y-3">
      {/* Materiality filter */}
      <div className="flex items-center gap-4 flex-wrap">
        <span className="text-sm font-medium text-gray-700 w-20">Priority:</span>
        <div className="flex gap-2">
          {levels.map((level) => {
            const isSelected = selectedMateriality.includes(level);
            const colors = materialityColors[level];
            return (
              <button
                key={level}
                onClick={() => toggleMateriality(level)}
                className={`
                  inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors border
                  ${isSelected
                    ? `${colors.selectedBg} ${colors.text} border-current`
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}
                `}
              >
                <span className={`mr-1.5 h-1.5 w-1.5 rounded-full ${colors.dot}`} />
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Event type filter */}
      {availableEventTypes.length > 0 && (
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-sm font-medium text-gray-700 w-20">Type:</span>
          <div className="flex gap-2 flex-wrap">
            {availableEventTypes.map((type) => {
              const isSelected = selectedEventTypes.includes(type);
              return (
                <button
                  key={type}
                  onClick={() => toggleEventType(type)}
                  className={`
                    inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors border
                    ${isSelected
                      ? 'bg-primary-100 text-primary-700 border-primary-300'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}
                  `}
                >
                  {eventTypeLabels[type] || type}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function NewsTab({ companyId }: NewsTabProps) {
  const [materialityFilter, setMaterialityFilter] = useState<Materiality[]>([]);
  const [eventTypeFilter, setEventTypeFilter] = useState<string[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  const { data: newsFeed, isLoading, error } = useCompanyNews(companyId, {
    days: 30,
    materiality: materialityFilter.length > 0 ? materialityFilter : undefined,
    limit: 50,
  });

  // PERF: Memoize events to maintain stable reference
  const allEvents = useMemo(() => newsFeed?.events || [], [newsFeed?.events]);

  // Get unique event types from the data for filter options
  // PERF: Memoize to avoid recreating Set on every render
  const availableEventTypes = useMemo(
    () => [...new Set(allEvents.map(e => e.event_type))],
    [allEvents]
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse rounded-lg border border-gray-200 bg-white p-4">
            <div className="h-4 w-24 bg-gray-200 rounded mb-2"></div>
            <div className="h-5 w-3/4 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 w-full bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <h3 className="text-sm font-medium text-red-800">Error loading news</h3>
        <div className="mt-2 text-sm text-red-700">
          {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      </div>
    );
  }

  // Apply event type filter client-side
  const events = allEvents.filter(event => {
    if (eventTypeFilter.length === 0) return true;
    return eventTypeFilter.includes(event.event_type);
  });

  return (
    <div className="space-y-6">
      {/* Filters */}
      <NewsFilter
        selectedMateriality={materialityFilter}
        onMaterialityChange={setMaterialityFilter}
        selectedEventTypes={eventTypeFilter}
        onEventTypeChange={setEventTypeFilter}
        availableEventTypes={availableEventTypes}
      />

      {/* News feed */}
      <div className="space-y-4">
        {events.map((event) => (
          <NewsItem
            key={event.id}
            event={event}
            onClick={() => setSelectedEventId(event.id)}
          />
        ))}
      </div>

      {/* News detail modal */}
      <NewsDetailModal
        eventId={selectedEventId}
        onClose={() => setSelectedEventId(null)}
      />

      {/* Empty state */}
      {events.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No news found.</p>
          {(materialityFilter.length > 0 || eventTypeFilter.length > 0) && (
            <p className="text-sm text-gray-400 mt-2">
              Try adjusting your filters.
            </p>
          )}
        </div>
      )}

      {/* Load more */}
      {newsFeed?.has_more && (
        <div className="text-center">
          <button className="text-sm text-primary-600 hover:text-primary-500 font-medium">
            Load more
          </button>
        </div>
      )}
    </div>
  );
}

export default NewsTab;
